import { useEffect, useMemo, useRef, useState } from "react";

import { useCatalog } from "../../catalog/CatalogContext";
import { buildProductSlug } from "../../data/products";
import { getSiteUrl } from "../../utils/siteUrl";
import { CartItem } from "../../cart/cartTypes";
import { formatPrice, getCartTotal } from "../../cart/cartUtils";

type CartCheckoutModalProps = {
  isOpen: boolean;
  items: CartItem[];
  onClose: () => void;
  onComplete: () => void;
};

type CheckoutForm = {
  phone: string;
  address: string;
  note: string;
};

const emptyForm: CheckoutForm = {
  phone: "",
  address: "",
  note: "",
};

export default function CartCheckoutModal({
  isOpen,
  items,
  onClose,
  onComplete,
}: CartCheckoutModalProps) {
  const { products, store } = useCatalog();
  const [form, setForm] = useState(emptyForm);
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  const total = useMemo(() => getCartTotal(items, products), [items, products]);

  useEffect(() => {
    if (!isOpen) return;

    setForm(emptyForm);
    const focusTimer = window.setTimeout(() => {
      firstInputRef.current?.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isDisabled = !form.phone || !form.address || items.length === 0;
  const siteUrl = getSiteUrl();

  const sendToWhatsApp = () => {
    const lines = items
      .map((item) => {
        const product = products.find((p) => p.id === item.productId);
        if (!product) return null;
        const size = item.size ? ` | Pointure ${item.size}` : "";
        const productUrl = `${siteUrl}/product/${buildProductSlug(product)}`;
        return `• ${product.title}${size} x${item.quantity} - ${product.price}\n  ${productUrl}`;
      })
      .filter(Boolean)
      .join("\n");

    const message = `
Bonjour
Je souhaite commander :

${lines}

Total estime : ${formatPrice(total)}
Telephone : ${form.phone}
Adresse : ${form.address}
Note : ${form.note || "Aucune"}
${store.instructions ? `Instructions : ${store.instructions}\n` : ""}
    `;

    const url = `https://wa.me/${store.whatsappNumber}?text=${encodeURIComponent(
      message
    )}`;

    window.open(url, "_blank");
    onComplete();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(20,10,5,0.64)] px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-title"
      onClick={onClose}
    >
      <div
        className="surface-panel relative w-full max-w-lg rounded-[34px] p-6 md:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(62,35,24,0.1)] bg-white/70 text-xl text-[#2c1b15]"
          aria-label="Fermer"
        >
          ×
        </button>

        <p className="brand-eyebrow">Finaliser</p>
        <h3 id="checkout-title" className="brand-display mt-4 text-3xl font-bold text-[#21140f]">
          Finaliser la commande
        </h3>
        <p className="mt-3 text-sm leading-7 text-[#725c50]">
          {store.name} vous contacte rapidement pour confirmer votre commande.
        </p>

        <div className="mt-6 rounded-[24px] bg-[#241712] px-5 py-4 text-[#fff1e5]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#e1c1ad]">
            Total
          </p>
          <p className="brand-display mt-2 text-3xl font-bold">
            {formatPrice(total)}
          </p>
        </div>

        <div className="mt-6 grid gap-4">
          <input
            ref={firstInputRef}
            id="checkout-phone"
            type="tel"
            placeholder="Numero de telephone"
            className="rounded-[20px] border border-[rgba(62,35,24,0.1)] bg-white/80 px-4 py-4 text-sm text-[#201410] outline-none placeholder:text-[#9f8576]"
            value={form.phone}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
          />

          <input
            id="checkout-address"
            type="text"
            placeholder="Adresse de livraison"
            className="rounded-[20px] border border-[rgba(62,35,24,0.1)] bg-white/80 px-4 py-4 text-sm text-[#201410] outline-none placeholder:text-[#9f8576]"
            value={form.address}
            onChange={(event) => setForm({ ...form, address: event.target.value })}
          />

          <textarea
            id="checkout-note"
            placeholder="Note complementaire"
            rows={4}
            className="rounded-[20px] border border-[rgba(62,35,24,0.1)] bg-white/80 px-4 py-4 text-sm text-[#201410] outline-none placeholder:text-[#9f8576]"
            value={form.note}
            onChange={(event) => setForm({ ...form, note: event.target.value })}
          />

          <button
            type="button"
            onClick={sendToWhatsApp}
            disabled={isDisabled}
            className="cta-primary rounded-full px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] disabled:opacity-40"
          >
            Confirmer la commande
          </button>
        </div>
      </div>
    </div>
  );
}
